import React, { useState } from 'react';
import { CheckIcon, ClockIcon, UserPlusIcon } from 'lucide-react';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { timezones } from '../../data/alerts';
export function Settings() {
  const {
    profile,
    updateProfile,
    plan,
    teamSeats
  } = useAccount();
  const [name, setName] = useState(profile.name);
  const [email, setEmail] = useState(profile.email);
  const [company, setCompany] = useState(profile.company);
  const [saved, setSaved] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    updateProfile({
      name,
      email,
      company
    });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  }
  function savePassword(event: React.FormEvent) {
    event.preventDefault();
    if (currentPassword.length < 8) {
      setPasswordError('Enter your current password.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New passwords are at least 8 characters.');
      return;
    }
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setPasswordSaved(true);
    window.setTimeout(() => setPasswordSaved(false), 3000);
  }
  return <div className="space-y-5" data-id="element-1194" data-mp="2yfedgry262ww">
      <div data-id="element-1195" data-mp="2yemvijxdw3p3">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-1196" data-mp="2yed4qu4ilsic">
          Settings
        </h1>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1197" data-mp="2ud1k730hzxh0">
          Your account, your time zone, and who else can see these monitors.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2 lg:items-start" data-id="element-1198" data-mp="12c08wpb059yl">
        <Panel as="section" data-id="element-1199" data-mp="174b4xh2mgblv">
          <PanelHeader title="Profile" data-id="element-1200" data-mp="1752vuel9xl5t" />
          <form onSubmit={saveProfile} className="space-y-4 px-5 py-5" noValidate data-id="element-1201" data-mp="176dvc27os6r4">
            <Field label="Name" htmlFor="profile-name" data-id="element-1202" data-mp="1c2co0201saxc">
              <TextInput id="profile-name" value={name} onChange={event => setName(event.target.value)} data-id="element-1203" data-mp="eg5o35eov6rb" />
            </Field>
            <Field label="Account email" htmlFor="profile-email" hint="Used for billing and account notices, not necessarily alerts." data-id="element-1204" data-mp="od0ibc3xikzs">
              <TextInput id="profile-email" type="email" value={email} onChange={event => setEmail(event.target.value)} data-id="element-1205" data-mp="1zsywfudyrx7v" />
            </Field>
            <Field label="Company" htmlFor="profile-company" data-id="element-1206" data-mp="3pa40qvkwfnwu">
              <TextInput id="profile-company" value={company} onChange={event => setCompany(event.target.value)} data-id="element-1207" data-mp="3karb632n82qk" />
            </Field>
            <div className="flex items-center gap-3" data-id="element-1208" data-mp="3aky156i5fvof">
              <Button type="submit" data-id="element-1209" data-mp="3ak52ytx9aehw">Save profile</Button>
              {saved && <span className="flex items-center gap-1.5 text-[13px] font-medium text-healthy-ink" data-id="element-1210" data-mp="35mwhajcwfsbw">
                  <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1211" data-mp="31j6u9ybvhisz" />
                  Saved
                </span>}
            </div>
          </form>
        </Panel>

        <div className="space-y-5" data-id="element-1212" data-mp="2rmvirak3wwzl">
          <Panel as="section" data-id="element-1213" data-mp="2rnwttf14hjhb">
            <PanelHeader title="Time zone" description="Everything you see, and the hour the daily sweep reports in." data-id="element-1214" data-mp="uts9ex7rzrpo" />
            <div className="px-5 py-5" data-id="element-1215" data-mp="pv8lir9mg5uo">
              <Field label="Your time zone" htmlFor="timezone" data-id="element-1216" data-mp="3jmp2ijfs5aaw">
                <SelectInput id="timezone" value={profile.timezone} onChange={event => updateProfile({
                timezone: event.target.value
              })} options={timezones.map(zone => ({
                value: zone,
                label: zone.replace('_', ' ')
              }))} data-id="element-1217" data-mp="3jngod2jkrtnz" />
              </Field>
              <p className="mt-3 flex items-start gap-2 rounded-control border border-line bg-canvas px-3.5 py-3 text-[13px] leading-relaxed text-ink-muted" data-id="element-1218" data-mp="34pel6tggha79">
                <ClockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1219" data-mp="2qzkwqc8v4ry2" />
                Certificate checks run daily at 08:00 in {profile.timezone}.
                Uptime pings run continuously on each monitor&apos;s own
                interval.
              </p>
            </div>
          </Panel>

          <Panel as="section" data-id="element-1220" data-mp="22cd6cvompy09">
            <PanelHeader title="Password" data-id="element-1221" data-mp="22dn4tlze987f" />
            <form onSubmit={savePassword} className="space-y-4 px-5 py-5" noValidate data-id="element-1222" data-mp="1ybt8fpw8eqd2">
              <Field label="Current password" htmlFor="current-password" data-id="element-1223" data-mp="1ydw5gtvsxjra">
                <TextInput id="current-password" type="password" autoComplete="current-password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} data-id="element-1224" data-mp="2ccm80p1e4bn1" />
              </Field>
              <Field label="New password" htmlFor="new-password" error={passwordError ?? undefined} data-id="element-1225" data-mp="2h8b8qvp5bykx">
                <TextInput id="new-password" type="password" autoComplete="new-password" value={newPassword} invalid={Boolean(passwordError)} onChange={event => setNewPassword(event.target.value)} data-id="element-1226" data-mp="2q40pusjka6si" />
              </Field>
              <div className="flex items-center gap-3" data-id="element-1227" data-mp="3c36dmk6g492b">
                <Button type="submit" variant="secondary" data-id="element-1228" data-mp="3c3y79pghfwpy">
                  Update password
                </Button>
                {passwordSaved && <span className="flex items-center gap-1.5 text-[13px] font-medium text-healthy-ink" data-id="element-1229" data-mp="3lspz7xljmh0v">
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1230" data-mp="3qsm2fumf24fd" />
                    Password changed
                  </span>}
              </div>
            </form>
          </Panel>
        </div>
      </div>

      <Panel as="section" data-id="element-1231" data-mp="3ooamwvd3jpk">
        <PanelHeader title="Team" description={plan.teamSeats > 1 ? `${plan.name} includes ${plan.teamSeats} seats. Everyone sees the same monitors.` : `${plan.name} is a single-seat plan. Pro adds 5 seats.`} action={plan.teamSeats > 1 ? <Button variant="secondary" size="sm" data-id="element-1233" data-mp="22utpmeq5vu8k">
                <UserPlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1234" data-mp="22weje24nj95d" />
                Invite someone
              </Button> : <Button variant="secondary" size="sm" to="/app/billing" data-id="element-1235" data-mp="1t3z1t5x9oml0">
                Move to Pro
              </Button>} data-id="element-1232" data-mp="3la7hmevj6h2" />
        {plan.teamSeats > 1 ? <ul className="divide-y divide-line" data-id="element-1236" data-mp="2lsw6254n8iur">
            {teamSeats.map(member => <li key={member.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-id="element-1237" data-mp="2gu31j27c426q">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-canvas text-[11px] font-semibold text-ink-muted" data-id="element-1238" data-mp="2btounnhgyyaa">
                  {member.name.split(' ').map(part => part[0]).join('')}
                </span>
                <span className="min-w-0 flex-1" data-id="element-1239" data-mp="vf6smz9a6077">
                  <span className="block truncate text-[13px] font-medium text-ink" data-id="element-1240" data-mp="1l55grhlleavm">
                    {member.name}
                  </span>
                  <span className="block truncate text-xs text-ink-soft" data-id="element-1241" data-mp="1q4rwpyst2584">
                    {member.email}
                  </span>
                </span>
                <span className="shrink-0 rounded-full border border-line bg-canvas px-2 py-0.5 text-[11px] font-medium text-ink-muted" data-id="element-1242" data-mp="1g5j1z3q48mkp">
                  {member.role}
                </span>
              </li>)}
          </ul> : <p className="px-5 py-5 text-[13px] text-ink-muted" data-id="element-1243" data-mp="28tyd3f64mkqr">
            You are the only person on this account. Alert recipients do not
            need a seat — add them under Notifications.
          </p>}
      </Panel>

      <Panel as="section" className="border-critical-border" data-id="element-1244" data-mp="2mu97gs6nahgo">
        <PanelHeader title="Close this account" description="Monitoring stops immediately, and every certificate history and incident log is deleted. This cannot be undone." data-id="element-1245" data-mp="2msofnabptcmi" />
        <div className="px-5 py-5" data-id="element-1246" data-mp="g45wfvqokxi3">
          <Field label="Type DELETE to confirm" htmlFor="confirm-delete" className="max-w-xs" data-id="element-1247" data-mp="b6w1k0kivtmv">
            <TextInput id="confirm-delete" value={confirmDelete} placeholder="DELETE" onChange={event => setConfirmDelete(event.target.value)} data-id="element-1248" data-mp="puh15ki6vaq1" />
          </Field>
          <Button variant="danger" className="mt-4" disabled={confirmDelete !== 'DELETE'} data-id="element-1249" data-mp="3telg5qgxwv85">
            Close account and stop monitoring
          </Button>
        </div>
      </Panel>
    </div>;
}